// Reads the Appretio tenancy claims off an access token. The token is a signed
// JWT validated server-side; here we only need to read the active org so the UI
// can show the current tenant. We decode the payload without verifying the
// signature (the server is the source of truth) — a tampered token only ever
// mislabels the selector, never grants access.

const NAMESPACE = "https://appretio.ai/";

/** The active organization carried by an access token. */
export interface TokenOrgClaims {
  /** Auth0 organization id (`org_...`), present on org-scoped tokens. */
  orgId: string | null;
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
  const json = atob(padded);
  // Handle UTF-8 payloads (org names are ASCII today, but be safe).
  return decodeURIComponent(
    json
      .split("")
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join("")
  );
}

/**
 * Decode the Appretio `org_id` claim from an access-token JWT. Returns a
 * `null` id when the token is absent, malformed, or not org-scoped (e.g. a
 * personal login before the user picks a tenant).
 */
export function readTokenOrgClaims(
  accessToken: string | undefined
): TokenOrgClaims {
  if (!accessToken) {
    return { orgId: null };
  }
  const parts = accessToken.split(".");
  if (parts.length < 2) {
    return { orgId: null };
  }
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1])) as Record<
      string,
      unknown
    >;
    const orgId = payload[`${NAMESPACE}org_id`];
    return {
      orgId: typeof orgId === "string" && orgId ? orgId : null
    };
  } catch {
    return { orgId: null };
  }
}
